import { createClient } from '@supabase/supabase-js';
import { 
  uploadToCloudinary, 
  deleteFromCloudinary,
  extractCloudinaryPublicId,
  getOptimizedImageUrl, 
  getAutoCropUrl, 
  getThumbnailUrl, 
  CLOUDINARY_CONFIG 
} from './cloudinaryClient.js';

export { 
  uploadToCloudinary, 
  deleteFromCloudinary,
  extractCloudinaryPublicId,
  getOptimizedImageUrl, 
  getAutoCropUrl, 
  getThumbnailUrl, 
  CLOUDINARY_CONFIG 
};

const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL || process?.env?.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY || process?.env?.VITE_SUPABASE_ANON_KEY;

export const isSupabaseConfigured = Boolean(
  supabaseUrl &&
  supabaseAnonKey &&
  !supabaseUrl.includes('tu-proyecto-id') &&
  supabaseUrl.startsWith('https://')
);

export const supabase = isSupabaseConfigured
  ? createClient(supabaseUrl, supabaseAnonKey)
  : null;

if (!isSupabaseConfigured) {
  console.warn(
    '[InstaPlay Latam] Supabase no está configurado aún o faltan variables en .env. ' +
    'Configura VITE_SUPABASE_URL y VITE_SUPABASE_ANON_KEY para habilitar el backend en vivo.'
  );
}

// =========================================================================
// PUBLIC API HELPERS (index.html & feedback.html)
// =========================================================================

/**
 * Obtener métricas públicas para el contador retro en vivo (auditables y 100% reales)
 */
export async function fetchPublicMetrics() {
  const zeroMetrics = {
    gamers_activos_online: 0,
    total_ventas_historicas: 0,
    porcentaje_renovacion: 0,
    racha_record_comunidad: 0
  };

  if (!isSupabaseConfigured || !supabase) {
    return zeroMetrics;
  }

  try {
    const { data, error } = await supabase
      .from('vista_metricas_publicas')
      .select('*')
      .maybeSingle();

    if (error || !data) {
      return zeroMetrics;
    }

    const totalVentas = Number(data.total_ventas_historicas) || 0;
    const gamersActivos = Number(data.gamers_activos_online) || 0;

    // Si no hay ventas o clientes registrados en el sistema, no hay renovaciones ni racha récord artificial
    const pctRenovacion = totalVentas > 0 ? (Number(data.porcentaje_renovacion) || 0) : 0;
    const rachaRecord = totalVentas > 0 ? (Number(data.racha_record_comunidad) || 0) : 0;

    return {
      gamers_activos_online: gamersActivos,
      total_ventas_historicas: totalVentas,
      porcentaje_renovacion: pctRenovacion,
      racha_record_comunidad: rachaRecord
    };
  } catch (err) {
    console.error('Error al obtener métricas públicas:', err);
    return zeroMetrics;
  }
}

/**
 * Obtener testimonios aprobados para la web pública (index.html)
 * Fusiona los aprobados en Supabase con los aprobados en caché local/panel admin.
 */
export async function fetchApprovedTestimonials() {
  let dbTestimonials = [];
  const deletedIds = getDeletedIds();

  if (isSupabaseConfigured && supabase) {
    try {
      const { data, error } = await supabase
        .from('testimonios')
        .select('*')
        .eq('is_approved', true)
        .order('created_at', { ascending: false });

      if (!error && data) {
        dbTestimonials = data.filter(t => !deletedIds.has(String(t.id)) && !isTestTestimonial(t));
      } else if (error) {
        console.warn('Advertencia al consultar testimonios aprobados en Supabase:', error);
      }
    } catch (err) {
      console.warn('Error al conectar con testimonios en Supabase:', err);
    }
  }

  // Cargar testimonios locales aprobados (aprobados desde instaplay-os-sysadmin.html)
  const localList = getLocalPendingTestimonials().filter(t => !deletedIds.has(String(t.id)) && !isTestTestimonial(t));
  const localApproved = localList.filter(t => t.is_approved);

  // Fusionar: los testimonios aprobados en el panel admin van primero
  const existingIds = new Set(dbTestimonials.map(t => String(t.id)));
  const existingKeys = new Set(dbTestimonials.map(t => `${(t.gamertag || '').toLowerCase().trim()}_${(t.testimonio_texto || '').toLowerCase().trim()}`));
  const merged = [...dbTestimonials];

  for (const item of localApproved) {
    const key = `${(item.gamertag || '').toLowerCase().trim()}_${(item.testimonio_texto || '').toLowerCase().trim()}`;
    if (!existingIds.has(String(item.id)) && !existingKeys.has(key) && !deletedIds.has(String(item.id))) {
      merged.unshift(item);
    }
  }

  // Intentar sincronizar en segundo plano los testimonios aprobados locales hacia Supabase
  if (isSupabaseConfigured && supabase && localApproved.length > 0) {
    syncLocalApprovedToSupabase().catch(() => {});
  }

  return merged.filter(t => !isTestTestimonial(t));
}

/**
 * Sincronizar testimonios aprobados locales hacia Supabase para que estén disponibles globalmente
 */
export async function syncLocalApprovedToSupabase() {
  if (!isSupabaseConfigured || !supabase) return;
  try {
    const localList = getLocalPendingTestimonials();
    const approved = localList.filter(t => t.is_approved);
    if (approved.length === 0) return;

    for (const item of approved) {
      const targetId = (item.id === 'test-pending-cloudinary-1') ? SEED_TEST_UUID : item.id;
      if (isValidUUID(targetId)) {
        await supabase
          .from('testimonios')
          .upsert([{ ...item, id: targetId }]);
      }
    }
  } catch (err) {
    console.warn('Sincronización en segundo plano con Supabase omitida:', err);
  }
}

/**
 * Subir comprobante al CDN de Cloudinary con auto-optimización (f_auto, q_auto)
 * Reemplaza el bucket local de Supabase Storage con compresión moderna y CDN global.
 */
export async function uploadComprobante(file) {
  try {
    const result = await uploadToCloudinary(file, {
      folder: 'instaplay_comprobantes',
      tags: 'instaplay,comprobante,testimonio'
    });
    return result.secure_url;
  } catch (cloudinaryErr) {
    console.warn('[Cloudinary] Fallo en subida a Cloudinary, intentando fallback de Supabase Storage si está activo:', cloudinaryErr);
    
    // Fallback de contingencia con Supabase Storage si el bucket existiese
    if (isSupabaseConfigured && supabase) {
      try {
        const cleanName = (file.name || 'voucher.jpg').replace(/[^a-zA-Z0-9.-]/g, '_');
        const path = `proof_${Date.now()}_${Math.random().toString(36).substring(2, 8)}_${cleanName}`;
        const { error } = await supabase.storage
          .from('comprobantes')
          .upload(path, file, { cacheControl: '3600', upsert: false });

        if (!error) {
          const { data: publicUrlData } = supabase.storage.from('comprobantes').getPublicUrl(path);
          return publicUrlData.publicUrl;
        }
      } catch (_) {}
    }
    throw cloudinaryErr;
  }
}

/**
 * Subir captura o evidencia de strike a Cloudinary CDN
 */
export async function uploadStrikeEvidence(file) {
  const result = await uploadToCloudinary(file, {
    folder: 'instaplay_strikes',
    tags: 'instaplay,strike,evidencia'
  });
  return result.secure_url;
}

export const isValidUUID = (id) => typeof id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id.trim());

export const generateSafeUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

export const SEED_TEST_UUID = '00000000-0000-4000-8000-000000000001';

export const LOCAL_PENDING_TESTIMONIALS_KEY = 'instaplay_pending_testimonials_cache';
export const LOCAL_DELETED_TESTIMONIALS_KEY = 'instaplay_deleted_testimonials_cache';

const FAKE_SEED_GAMERTAGS = new Set([
  'kevin elbryan_pe',
  'mateo elpana_gamer',
  'axel eltacos_fps',
  'camila kitsune_fps',
  'sebastián sebagamer_ar',
  'sebastian sebagamer_ar',
  'carlos charly_bogota',
  'valeria val_gamermx',
  'rodrigo rodri_arequipa',
  'andrés andy_quito',
  'andres andy_quito',
  'mariana mari_paz',
  'facundo facu_montevideo',
  'lucas luquitas_cba',
  'diego dieguito_gamer',
  'esteban teban_medellin',
  'gabriela gaby_monterrey',
  'ignacio nacho_valpo',
  'javier javi_asuncion',
  'daniela dani_cali',
  'matías mati_rosario',
  'matias mati_rosario',
  'pedro peter_trujillo',
  'alejandro ale_guat',
  'sofía sofi_bcn',
  'sofia sofi_bcn',
  'gonzalo gonza_cusco'
]);

/**
 * Detectar si un testimonio corresponde a datos de prueba, semillas de laboratorio o mocks
 */
export function isTestTestimonial(t) {
  if (!t) return false;
  const idStr = String(t.id || '');
  const gamertag = String(t.gamertag || '').toLowerCase().trim();
  const text = String(t.testimonio_texto || '').toLowerCase();
  const url = String(t.comprobante_captura_url || '').toLowerCase();

  // 1. Gamertags de semillas ficticias / placeholders
  if (FAKE_SEED_GAMERTAGS.has(gamertag)) return true;

  // 2. UUIDs o identificadores de prueba
  if (idStr === SEED_TEST_UUID || idStr === 'test-pending-cloudinary-1' || idStr === '99999999-9999-4999-a999-999999999999') return true;
  if (idStr.startsWith('test-') || idStr.startsWith('demo-') || idStr.startsWith('local-test-')) return true;

  // 3. Gamertags de prueba
  if (gamertag === '@gamer_test_cloud' || gamertag.includes('test_cloud') || gamertag.includes('gamer_test')) return true;
  if (gamertag.startsWith('@kevin_fps_') || gamertag.startsWith('@dani_cloud_') || gamertag.startsWith('@alex_warzone_') || gamertag.startsWith('@valen_gamer_') || gamertag.startsWith('@mati_latam_')) return true;

  // 4. Textos o descriptores de prueba
  if (text.includes('test de auditoría') || text.includes('test de auditoria') || text.includes('[test]')) return true;
  if (text.includes('comprobante subido y optimizado automáticamente')) return true;

  // 5. Marcadores de imágenes artificiales o fotos de stock Unsplash de prueba
  if (url.includes('unsplash.com') || url.includes('gemini_generated_image') || url.includes('test.jpg')) return true;

  return false;
}

export function getDeletedIds() {
  try {
    if (typeof localStorage !== 'undefined') {
      const raw = localStorage.getItem(LOCAL_DELETED_TESTIMONIALS_KEY);
      if (!raw) return new Set();
      const list = JSON.parse(raw);
      if (Array.isArray(list)) return new Set(list.map(String));
    }
  } catch (_) {}
  return new Set();
}

export function recordDeletedId(id) {
  if (!id) return;
  try {
    if (typeof localStorage !== 'undefined') {
      const set = getDeletedIds();
      set.add(String(id));
      if (id === 'test-pending-cloudinary-1') set.add(SEED_TEST_UUID);
      if (id === SEED_TEST_UUID) set.add('test-pending-cloudinary-1');
      localStorage.setItem(LOCAL_DELETED_TESTIMONIALS_KEY, JSON.stringify(Array.from(set)));
    }
  } catch (_) {}
}

export function getLocalPendingTestimonials() {
  try {
    if (typeof localStorage !== 'undefined') {
      const raw = localStorage.getItem(LOCAL_PENDING_TESTIMONIALS_KEY);
      if (!raw) return [];
      const list = JSON.parse(raw);
      if (!Array.isArray(list)) return [];

      let modified = false;
      const sanitized = list.map(item => {
        if (!item) return item;
        let cUrl = item.comprobante_captura_url;
        let id = item.id;
        if (cUrl && cUrl.includes('test.jpg')) {
          modified = true;
          cUrl = 'https://res.cloudinary.com/afkfwjkg/image/upload/f_auto,q_auto/v1789306843/instaplay_comprobantes/aaovr08lnwpwbch0yab0.jpg';
        }
        // Migrar id antiguos tipo 'test-pending-cloudinary-1' a UUID estándar válido
        if (!id || !isValidUUID(id)) {
          modified = true;
          id = (id === 'test-pending-cloudinary-1') ? SEED_TEST_UUID : generateSafeUUID();
        }
        return {
          ...item,
          id,
          comprobante_captura_url: cUrl
        };
      }).filter(Boolean);

      // Limpieza automática en caliente de testimonios de prueba para producción
      const productionClean = sanitized.filter(t => !isTestTestimonial(t));
      if (productionClean.length !== sanitized.length) {
        modified = true;
      }

      if (modified) {
        saveLocalPendingTestimonials(productionClean);
      }
      return productionClean;
    }
  } catch (_) {}
  return [];
}

export function saveLocalPendingTestimonials(list) {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(LOCAL_PENDING_TESTIMONIALS_KEY, JSON.stringify(list));
    }
  } catch (_) {}
}

/**
 * Enviar nuevo testimonio desde /feedback.html
 * Guarda de inmediato en caché local y sincroniza con Supabase
 */
export async function submitTestimonial(testimonialData) {
  const validClientId = (testimonialData.cliente_id && isValidUUID(testimonialData.cliente_id))
    ? testimonialData.cliente_id.trim()
    : null;

  const newId = (testimonialData.id && isValidUUID(testimonialData.id))
    ? testimonialData.id
    : generateSafeUUID();

  const payload = {
    id: newId,
    cliente_id: validClientId,
    gamertag: testimonialData.gamertag,
    perfil_social_link: testimonialData.perfil_social_link || null,
    ciudad_pais: testimonialData.ciudad_pais,
    calificacion_estrellas: testimonialData.calificacion_estrellas || 5,
    juegos_mencionados: testimonialData.juegos_mencionados || '',
    dispositivo_red: testimonialData.dispositivo_red || '',
    testimonio_texto: testimonialData.testimonio_texto,
    comprobante_captura_url: testimonialData.comprobante_captura_url || null,
    insignia_verificacion: testimonialData.insignia_verificacion || 'PAGO_VERIFICADO_YAPE',
    racha_al_publicar: testimonialData.racha_al_publicar || 1,
    is_approved: false,
    created_at: new Date().toISOString()
  };

  // 1. Guardar de inmediato en almacenamiento local para garantizar que el panel admin lo detecte siempre
  try {
    const localList = getLocalPendingTestimonials();
    // Evitar duplicados
    const exists = localList.some(t => t.gamertag === payload.gamertag && t.testimonio_texto === payload.testimonio_texto);
    if (!exists) {
      localList.unshift(payload);
      saveLocalPendingTestimonials(localList);
    }
  } catch (localErr) {
    console.warn('Error al guardar en caché local:', localErr);
  }

  // 2. Si Supabase está disponible, registrar también en base de datos
  if (isSupabaseConfigured && supabase) {
    try {
      const { error } = await supabase
        .from('testimonios')
        .insert([payload]);

      if (error) {
        console.warn('Advertencia al insertar en Supabase (guardado local preservado):', error);
      }

      // Si se envió con cliente_id válido en BD, marcar encuesta como enviada
      if (validClientId) {
        try {
          const { error: rpcErr } = await supabase.rpc('fn_completar_encuesta_cliente', {
            p_cliente_id: validClientId
          });
          if (rpcErr) {
            await supabase
              .from('clientes')
              .update({ encuesta_15d_enviada: true })
              .eq('id', validClientId);
          }
        } catch (_) {}
      }
    } catch (dbErr) {
      console.warn('Fallo de conexión con Supabase (guardado local preservado):', dbErr);
    }
  }

  return { success: true, payload };
}

/**
 * Buscar datos del cliente por ID (para precargar /feedback.html?id=...)
 */
export async function getClientPublicInfo(clientId) {
  if (!clientId) return null;
  const isDemo = clientId.toLowerCase() === 'demo' || clientId.toLowerCase() === 'demo-uuid-client' || clientId.toLowerCase().startsWith('demo');
  if (isDemo) {
    return {
      id: 'demo-uuid-client',
      nombre_completo: 'Gamer Verificado (Demo)',
      cuenta_asignada: 'demo@instaplay.latam',
      racha_meses: 2,
      pais: 'Perú',
      encuesta_15d_enviada: false,
      fecha_inicio: '2026-08-01'
    };
  }
  const isValidUUID = (id) => typeof id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id.trim());
  if (!isSupabaseConfigured || !supabase || !isValidUUID(clientId)) return null;
  try {
    // 1. Intentar llamar a la función RPC segura fn_obtener_info_feedback
    const { data: rpcData, error: rpcErr } = await supabase.rpc('fn_obtener_info_feedback', {
      p_cliente_id: clientId.trim()
    });
    if (!rpcErr && rpcData && rpcData.length > 0) {
      return rpcData[0];
    }

    // 2. Fallback estándar a SELECT restringido por columnas
    const { data, error } = await supabase
      .from('clientes')
      .select('id, nombre_completo, cuenta_asignada, racha_meses, pais, encuesta_15d_enviada, fecha_inicio')
      .eq('id', clientId.trim())
      .maybeSingle();
    if (error) {
      console.warn('Error al verificar cliente:', error);
      return null;
    }
    return data;
  } catch (err) {
    console.warn('Excepción al verificar cliente:', err);
    return null;
  }
}

// =========================================================================
// ADMIN API HELPERS (/instaplay-os-sysadmin.html)
// =========================================================================

export async function adminLogin(email, password) {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error('Supabase no está configurado en .env');
  }
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password
  });
  if (error) throw error;
  return data;
}

export async function adminGetSession() {
  if (!isSupabaseConfigured || !supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data?.session || null;
}

export async function adminLogout() {
  if (!isSupabaseConfigured || !supabase) return;
  await supabase.auth.signOut();
}

export async function adminGetAlerts() {
  if (!isSupabaseConfigured || !supabase) return [];
  const { data, error } = await supabase
    .from('vista_alertas_admin')
    .select('*')
    .neq('tipo_alerta', 'NORMAL')
    .order('fecha_vencimiento', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function adminGetClients() {
  if (!isSupabaseConfigured || !supabase) return [];
  const { data, error } = await supabase
    .from('clientes')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function adminCreateClient(client) {
  if (!isSupabaseConfigured || !supabase) throw new Error('Supabase no configurado');

  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('No hay una sesión activa de administrador. Por favor inicia sesión primero en el panel.');
  }

  let data = null;
  let insertError = null;

  try {
    const res = await supabase
      .from('clientes')
      .insert([client])
      .select()
      .single();
    data = res.data;
    insertError = res.error;
  } catch (e) {
    insertError = e;
  }

  // Si alguna columna de las migraciones aún no fue corrida en Supabase, reintentar limpiando campos opcionales
  if (insertError && (
    insertError.message?.includes('cupon_usado') ||
    insertError.message?.includes('has_salsanow') ||
    insertError.message?.includes('storage_capacity') ||
    insertError.message?.includes('monthly_hours') ||
    insertError.message?.includes('referral_code') ||
    insertError.message?.includes('referred_by_code') ||
    insertError.message?.includes('referrals_count') ||
    insertError.code === 'PGRST204'
  )) {
    console.warn('Algunas columnas nuevas no existen en Supabase aún. Reintentando registro con columnas base...', insertError.message);
    const clientCopy = { ...client };
    delete clientCopy.cupon_usado;
    delete clientCopy.has_salsanow;
    delete clientCopy.storage_capacity;
    delete clientCopy.monthly_hours;
    delete clientCopy.referral_code;
    delete clientCopy.referred_by_code;
    delete clientCopy.referrals_count;
    const retryRes = await supabase
      .from('clientes')
      .insert([clientCopy])
      .select()
      .single();
    if (retryRes.error) throw retryRes.error;
    data = retryRes.data;
    insertError = null;
  } else if (insertError) {
    throw insertError;
  }

  // Registrar primer pago en historial con la fecha de inicio/registro
  if (data && data.id) {
    const paymentTimestamp = client.fecha_inicio 
      ? new Date(client.fecha_inicio + 'T12:00:00Z').toISOString() 
      : new Date().toISOString();

    const pagoData = {
      cliente_id: data.id,
      monto_usd: client.total_gastado_usd || 10.00,
      divisa: client.divisa || 'USD',
      monto_cobrado: client.monto_cobrado || client.total_gastado_usd || 10.00,
      metodo_pago: client.metodo_pago || 'Yape',
      numero_mes: 1,
      tipo_pago: 'SUSCRIPCION',
      concepto: 'Alta de cuenta y suscripción inicial' + (client.has_salsanow ? ' + SalsaNow' : '') + (client.storage_capacity === '1TB' ? ' + 1TB' : '') + (client.monthly_hours === 150 ? ' + 50h Extra' : ''),
      fecha_pago: paymentTimestamp
    };

    try {
      await supabase.from('historial_pagos').insert([pagoData]);
    } catch {
      // Reintentar sin columnas nuevas si no existen
      delete pagoData.tipo_pago;
      delete pagoData.concepto;
      delete pagoData.divisa;
      delete pagoData.monto_cobrado;
      await supabase.from('historial_pagos').insert([pagoData]);
    }
  }

  return data;
}

export async function adminUpdateClient(clientId, updates) {
  if (!isSupabaseConfigured || !supabase) throw new Error('Supabase no configurado');
  const { data, error } = await supabase
    .from('clientes')
    .update(updates)
    .eq('id', clientId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

/**
 * Aplicar Upgrade / Mejoras a un cliente antiguo o existente
 */
export async function adminUpgradeClient(clientId, {
  has_salsanow,
  storage_capacity,
  monthly_hours,
  monto_usd = 0,
  monto_cobrado = null,
  divisa = 'USD',
  metodo_pago = 'Yape',
  concepto = 'Mejora de cuenta (Plus)',
  tipo_pago = 'UPGRADE'
}) {
  if (!isSupabaseConfigured || !supabase) throw new Error('Supabase no configurado');

  // 1. Obtener cliente actual para acumular total_gastado_usd
  const { data: cliente, error: clientErr } = await supabase
    .from('clientes')
    .select('id, total_gastado_usd, has_salsanow, storage_capacity, monthly_hours, racha_meses')
    .eq('id', clientId)
    .single();

  if (clientErr) throw clientErr;

  const currentTotal = parseFloat(cliente.total_gastado_usd) || 0;
  const upgradeCost = parseFloat(monto_usd) || 0;
  const newTotal = currentTotal + upgradeCost;

  const updates = {
    total_gastado_usd: newTotal
  };
  if (typeof has_salsanow === 'boolean') updates.has_salsanow = has_salsanow;
  if (storage_capacity) updates.storage_capacity = storage_capacity;
  if (monthly_hours) updates.monthly_hours = parseInt(monthly_hours, 10);

  let updatedClient = null;
  try {
    const { data: upRes, error: upErr } = await supabase
      .from('clientes')
      .update(updates)
      .eq('id', clientId)
      .select()
      .single();
    if (upErr) throw upErr;
    updatedClient = upRes;
  } catch (errUp) {
    // Si alguna columna no existe en Supabase aún, reintentar solo con total_gastado_usd
    console.warn('Error al actualizar columnas de upgrade, reintentando básico:', errUp);
    const { data: fallbackRes, error: fbErr } = await supabase
      .from('clientes')
      .update({ total_gastado_usd: newTotal })
      .eq('id', clientId)
      .select()
      .single();
    if (fbErr) throw fbErr;
    updatedClient = fallbackRes;
  }

  // 2. Registrar cobro del upgrade en historial de pagos
  const pagoRecord = {
    cliente_id: clientId,
    monto_usd: upgradeCost,
    divisa: divisa || 'USD',
    monto_cobrado: monto_cobrado !== null ? parseFloat(monto_cobrado) : upgradeCost,
    metodo_pago: metodo_pago || 'Yape',
    tipo_pago: tipo_pago || 'UPGRADE',
    concepto: concepto || 'Mejora de cuenta (Plus)',
    numero_mes: cliente.racha_meses || 1,
    fecha_pago: new Date().toISOString()
  };

  try {
    await supabase.from('historial_pagos').insert([pagoRecord]);
  } catch (errPago) {
    const fallbackPago = { ...pagoRecord };
    delete fallbackPago.tipo_pago;
    delete fallbackPago.concepto;
    delete fallbackPago.divisa;
    delete fallbackPago.monto_cobrado;
    try {
      await supabase.from('historial_pagos').insert([fallbackPago]);
    } catch (e2) {
      console.warn('No se pudo insertar en historial_pagos:', e2);
    }
  }

  return updatedClient;
}

/**
 * Buscar cliente por código de referido
 */
export async function adminFindClientByReferralCode(code) {
  if (!code || !isSupabaseConfigured || !supabase) return null;
  const cleanCode = code.trim().toUpperCase();
  try {
    const { data, error } = await supabase
      .from('clientes')
      .select('id, nombre_completo, whatsapp, referral_code, referrals_count')
      .ilike('referral_code', cleanCode)
      .maybeSingle();
    if (error) return null;
    return data;
  } catch {
    return null;
  }
}

/**
 * Incrementar contador de referidos de un cliente
 */
export async function adminIncrementReferralCount(referrerId) {
  if (!referrerId || !isSupabaseConfigured || !supabase) return null;
  try {
    const { data: refClient } = await supabase
      .from('clientes')
      .select('id, referrals_count')
      .eq('id', referrerId)
      .maybeSingle();
    
    if (refClient) {
      const currentCount = parseInt(refClient.referrals_count, 10) || 0;
      await supabase
        .from('clientes')
        .update({ referrals_count: currentCount + 1 })
        .eq('id', referrerId);
    }
  } catch (e) {
    console.warn('Error incrementando referrals_count:', e);
  }
}

/**
 * Restablecer contador de referidos de un cliente para pruebas de laboratorio
 */
export async function adminResetReferralCount(referrerId, count = 0) {
  if (!referrerId || !isSupabaseConfigured || !supabase) return null;
  try {
    const { data } = await supabase
      .from('clientes')
      .update({ referrals_count: Math.max(0, count) })
      .eq('id', referrerId)
      .select('id, nombre_completo, referrals_count, referral_code')
      .maybeSingle();
    return data;
  } catch (e) {
    console.warn('Error reiniciando referrals_count:', e);
    return null;
  }
}

export async function adminDeleteClient(clientId) {
  if (!isSupabaseConfigured || !supabase) throw new Error('Supabase no configurado');
  const { error } = await supabase
    .from('clientes')
    .delete()
    .eq('id', clientId);
  if (error) throw error;
  return true;
}

/**
 * Obtener saludo público básico para /entrega.html?id=...
 */
export async function getClientPublicGreeting(clientId) {
  if (!clientId) return null;
  const isDemo = clientId.toLowerCase() === 'demo' || clientId.toLowerCase() === 'demo-uuid-client' || clientId.toLowerCase().startsWith('demo');
  if (isDemo) {
    return { nombre_completo: 'Gamer Verificado (Demo)', pais: 'Perú' };
  }
  const isValidUUID = (id) => typeof id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id.trim());
  if (!isSupabaseConfigured || !supabase || !isValidUUID(clientId)) return null;
  try {
    const { data } = await supabase
      .from('clientes')
      .select('id, nombre_completo, pais')
      .eq('id', clientId.trim())
      .maybeSingle();
    return data;
  } catch {
    return null;
  }
}

/**
 * Desbloquear credenciales de juego en /entrega.html mediante desafío de teléfono (RPC seguro)
 */
export async function unlockClientCredentials(clientId, phoneValidator) {
  if (!clientId) return null;
  const isDemo = clientId.toLowerCase() === 'demo' || clientId.toLowerCase() === 'demo-uuid-client' || clientId.toLowerCase().startsWith('demo');
  if (isDemo || !isSupabaseConfigured || !supabase) {
    if (isDemo) {
      const cleanPhone = (phoneValidator || '').replace(/\D/g, '');
      if (cleanPhone.length >= 4) {
        return {
          id: 'demo-uuid-client',
          nombre_completo: 'Gamer Verificado (Demo)',
          cuenta_asignada: 'demo@instaplay.latam',
          password_cuenta: 'InstaPlay#Demo2026!',
          fecha_inicio: '2026-09-01',
          fecha_vencimiento: '2026-10-01',
          pais: 'Perú',
          has_salsanow: true,
          storage_capacity: '1TB',
          monthly_hours: 150,
          referral_code: 'PLAY-DEMO77',
          referrals_count: 2
        };
      }
      return null;
    }
    return null;
  }

  try {
    // 1. Intentar llamar a la función RPC segura en Postgres
    const { data, error } = await supabase.rpc('fn_desbloquear_credenciales_entrega', {
      p_cliente_id: clientId,
      p_telefono_validador: phoneValidator
    });

    if (!error && data && data.length > 0) {
      return data[0];
    }

    if (error) {
      console.warn('Fallo en RPC de credenciales (posible falta de actualización de SQL en BD):', error);
    }

    // 2. Fallback de contingencia: si la función RPC aún no ha sido ejecutada en Supabase
    // consultar el cliente directamente por ID y verificar coincidencia de teléfono
    let client = null;
    const { data: directClient, error: directErr } = await supabase
      .from('clientes')
      .select('id, nombre_completo, whatsapp, cuenta_asignada, password_cuenta, fecha_inicio, fecha_vencimiento, pais, has_salsanow, storage_capacity, monthly_hours, referral_code, referrals_count')
      .eq('id', clientId)
      .maybeSingle();

    if (directErr) {
      // Reintentar sin password_cuenta o columnas nuevas por si la columna aún no existe en su base de datos
      const { data: legacyClient, error: legErr } = await supabase
        .from('clientes')
        .select('id, nombre_completo, whatsapp, cuenta_asignada, fecha_inicio, fecha_vencimiento, pais')
        .eq('id', clientId)
        .maybeSingle();
      if (!legErr && legacyClient) {
        client = legacyClient;
      }
    } else {
      client = directClient;
    }

    if (client) {
      const cleanSaved = (client.whatsapp || '').replace(/\D/g, '');
      const cleanInput = (phoneValidator || '').replace(/\D/g, '');
      const cleanWithoutZeros = cleanInput.replace(/^0+/, '');

      const matches = cleanInput.length >= 4 && (
        cleanSaved === cleanInput ||
        cleanSaved.endsWith(cleanInput) ||
        (cleanWithoutZeros.length >= 4 && cleanSaved.endsWith(cleanWithoutZeros)) ||
        cleanSaved.slice(-4) === cleanInput.slice(-4)
      );

      if (matches) {
        return {
          id: client.id,
          nombre_completo: client.nombre_completo,
          cuenta_asignada: client.cuenta_asignada,
          password_cuenta: client.password_cuenta || 'InstaPlay#2026!',
          fecha_inicio: client.fecha_inicio,
          fecha_vencimiento: client.fecha_vencimiento,
          pais: client.pais,
          has_salsanow: Boolean(client.has_salsanow),
          storage_capacity: client.storage_capacity || '100GB',
          monthly_hours: client.monthly_hours || 100,
          referral_code: client.referral_code || null,
          referrals_count: client.referrals_count || 0
        };
      }
    }

    return null;
  } catch (err) {
    console.error('Error al desbloquear credenciales:', err);
    return null;
  }
}

export async function adminRenewClient(clientId, montoUsd = 10, metodoPago = 'Yape', nuevaFechaVencimiento = null) {
  if (!isSupabaseConfigured || !supabase) throw new Error('Supabase no configurado');

  // Obtener racha actual para determinar el número de mes
  const { data: cliente, error: clientErr } = await supabase
    .from('clientes')
    .select('racha_meses, fecha_vencimiento')
    .eq('id', clientId)
    .single();

  if (clientErr) throw clientErr;

  const nextMonth = (cliente.racha_meses || 1) + 1;

  // Insertar en historial_pagos (el trigger automático de Postgres actualiza racha, fecha y estado)
  const { data: pago, error: pagoErr } = await supabase
    .from('historial_pagos')
    .insert([{
      cliente_id: clientId,
      monto_usd: montoUsd,
      metodo_pago: metodoPago,
      numero_mes: nextMonth
    }])
    .select()
    .single();

  if (pagoErr) throw pagoErr;

  // Si se especificó una fecha de vencimiento personalizada o ajustada, actualizarla explícitamente
  if (nuevaFechaVencimiento) {
    await supabase
      .from('clientes')
      .update({ fecha_vencimiento: nuevaFechaVencimiento, estado: 'ACTIVO' })
      .eq('id', clientId);
  }

  return pago;
}

export async function adminGetTestimonials() {
  let dbTestimonials = [];
  const deletedIds = getDeletedIds();

  if (isSupabaseConfigured && supabase) {
    try {
      const { data, error } = await supabase
        .from('testimonios')
        .select('*')
        .order('created_at', { ascending: false });
      if (!error && data) {
        dbTestimonials = data.filter(t => !deletedIds.has(String(t.id)) && !isTestTestimonial(t));
      }
    } catch (err) {
      console.warn('Error al consultar testimonios en Supabase:', err);
    }
  }

  // Cargar testimonios locales (enviados desde feedback.html), filtrando de inmediato pruebas
  let localList = getLocalPendingTestimonials().filter(t => !deletedIds.has(String(t.id)) && !isTestTestimonial(t));

  // Fusionar: si un testimonio local no está en dbTestimonials (por RLS de Supabase o modo local), incluirlo
  const existingIds = new Set(dbTestimonials.map(t => String(t.id)));
  const merged = [...dbTestimonials];

  for (const item of localList) {
    if (item && item.id && !existingIds.has(String(item.id)) && !deletedIds.has(String(item.id)) && !isTestTestimonial(item)) {
      merged.unshift(item);
    }
  }

  return merged.filter(t => !isTestTestimonial(t));
}

/**
 * Generar un testimonio de prueba bajo demanda con foto de Cloudinary CDN
 */
export async function generateTestPendingTestimonial(customData = {}) {
  const newId = (customData.id && isValidUUID(customData.id))
    ? customData.id
    : generateSafeUUID();

  const sampleGamers = ['@kevin_fps', '@dani_cloud', '@alex_warzone', '@valen_gamer', '@mati_latam'];
  const randomGamer = sampleGamers[Math.floor(Math.random() * sampleGamers.length)];

  const payload = {
    id: newId,
    gamertag: customData.gamertag || `${randomGamer}_${Math.floor(10 + Math.random() * 90)}`,
    ciudad_pais: customData.ciudad_pais || 'Bogotá, Colombia',
    calificacion_estrellas: 5,
    juegos_mencionados: customData.juegos_mencionados || 'Forza Horizon 5, Warzone, Cyberpunk',
    dispositivo_red: customData.dispositivo_red || 'PC / Laptop · Fibra Óptica 300 Mbps',
    testimonio_texto: customData.testimonio_texto || 'Servicio verificado. Comprobante subido y optimizado automáticamente con Cloudinary CDN.',
    insignia_verificacion: customData.insignia_verificacion || 'PAGO_VERIFICADO_PAYPAL',
    racha_al_publicar: Math.floor(1 + Math.random() * 4),
    is_approved: false,
    comprobante_captura_url: customData.comprobante_captura_url || 'https://res.cloudinary.com/afkfwjkg/image/upload/f_auto,q_auto/v1789306843/instaplay_comprobantes/aaovr08lnwpwbch0yab0.jpg',
    created_at: new Date().toISOString()
  };

  const list = getLocalPendingTestimonials();
  list.unshift(payload);
  saveLocalPendingTestimonials(list);

  if (isSupabaseConfigured && supabase) {
    try {
      await supabase.from('testimonios').insert([payload]);
    } catch (err) {
      console.warn('Advertencia al sincronizar con Supabase (guardado local preservado):', err);
    }
  }

  return payload;
}

export async function adminApproveTestimonial(id, isApproved = true) {
  let updatedRecord = null;
  const targetId = (id === 'test-pending-cloudinary-1') ? SEED_TEST_UUID : id;

  // 1. Actualizar de inmediato en caché local
  const localList = getLocalPendingTestimonials();
  const idx = localList.findIndex(t => 
    String(t.id) === String(id) || 
    String(t.id) === String(targetId) ||
    (id === 'test-pending-cloudinary-1' && t.id === SEED_TEST_UUID)
  );
  if (idx !== -1) {
    localList[idx].is_approved = isApproved;
    if (!isValidUUID(localList[idx].id)) {
      localList[idx].id = (localList[idx].id === 'test-pending-cloudinary-1') ? SEED_TEST_UUID : generateSafeUUID();
    }
    saveLocalPendingTestimonials(localList);
    updatedRecord = localList[idx];
  }

  // 2. Actualizar o sincronizar en Supabase si está disponible y es un UUID válido
  if (isSupabaseConfigured && supabase && isValidUUID(targetId)) {
    try {
      const { data, error } = await supabase
        .from('testimonios')
        .update({ is_approved: isApproved })
        .eq('id', targetId)
        .select();

      if (!error && data && data.length > 0) {
        updatedRecord = data[0];
      } else if (!error && (!data || data.length === 0) && idx !== -1) {
        // El registro no existía previamente en Supabase; upsert para persistirlo en la nube
        try {
          const itemToUpsert = { ...localList[idx], id: targetId, is_approved: isApproved };
          const { data: upsertedData } = await supabase
            .from('testimonios')
            .upsert([itemToUpsert])
            .select();
          if (upsertedData && upsertedData.length > 0) {
            updatedRecord = upsertedData[0];
          }
        } catch (_) {}
      }
    } catch (err) {
      console.warn('No se pudo actualizar en Supabase (sincronizando local):', err);
    }
  }

  return updatedRecord || { id: targetId, is_approved: isApproved };
}

export async function adminDeleteTestimonial(id) {
  if (!id) return { success: false, reason: 'no_id' };
  const targetId = (id === 'test-pending-cloudinary-1') ? SEED_TEST_UUID : id;

  // 1. Registrar inmediatamente el ID en la lista persistente de eliminados para que no resucite
  recordDeletedId(id);
  recordDeletedId(targetId);

  // 2. Extraer la URL del comprobante para purgarla de Cloudinary CDN
  let proofUrl = null;
  const localList = getLocalPendingTestimonials();
  const localItem = localList.find(t => 
    String(t.id) === String(id) || 
    String(t.id) === String(targetId) ||
    (id === 'test-pending-cloudinary-1' && t.id === SEED_TEST_UUID)
  );

  if (localItem && localItem.comprobante_captura_url) {
    proofUrl = localItem.comprobante_captura_url;
  }

  // Consultar en Supabase si no se encontró en local o para asegurar el comprobante más reciente
  if (!proofUrl && isSupabaseConfigured && supabase && isValidUUID(targetId)) {
    try {
      const { data, error } = await supabase
        .from('testimonios')
        .select('comprobante_captura_url')
        .eq('id', targetId)
        .maybeSingle();
      if (!error && data && data.comprobante_captura_url) {
        proofUrl = data.comprobante_captura_url;
      }
    } catch (err) {
      console.warn('Advertencia al consultar comprobante para eliminación en Supabase:', err);
    }
  }

  // 3. Purgar la foto de comprobante en Cloudinary CDN (limpieza automática de almacenamiento)
  let cloudinaryResult = null;
  if (proofUrl) {
    try {
      const publicId = extractCloudinaryPublicId(proofUrl);
      if (publicId || proofUrl.includes('cloudinary.com')) {
        console.log(`[Cloudinary Cleanup] Eliminando comprobante en Cloudinary CDN: ${proofUrl} (publicId: ${publicId})`);
        cloudinaryResult = await deleteFromCloudinary(proofUrl);
        console.log(`[Cloudinary Cleanup] Resultado de eliminación:`, cloudinaryResult);
      }
    } catch (cdnErr) {
      console.warn('[Cloudinary Cleanup] Advertencia al eliminar imagen en Cloudinary:', cdnErr);
    }
  }

  // 4. Eliminar registro en Supabase si es un UUID válido
  if (isSupabaseConfigured && supabase && isValidUUID(targetId)) {
    try {
      await supabase.from('testimonios').delete().eq('id', targetId);
    } catch (err) {
      console.warn('No se pudo borrar en Supabase (sincronizando local):', err);
    }
  }

  // 5. Eliminar en caché local
  const filtered = localList.filter(t => 
    String(t.id) !== String(id) && 
    String(t.id) !== String(targetId) &&
    !(id === 'test-pending-cloudinary-1' && t.id === SEED_TEST_UUID)
  );
  saveLocalPendingTestimonials(filtered);

  return {
    success: true,
    id,
    targetId,
    cloudinaryDeleted: cloudinaryResult?.result === 'ok',
    cloudinaryResult
  };
}

/**
 * Purgar completamente todos los testimonios de prueba tanto de Supabase como de LocalStorage
 */
export async function adminPurgeAllTestTestimonials() {
  const result = {
    totalPurged: 0,
    cloudinaryDeleted: 0,
    purgedIds: []
  };

  // 1. Obtener lista completa actual
  let allTestimonials = [];
  if (isSupabaseConfigured && supabase) {
    try {
      const { data } = await supabase.from('testimonios').select('*');
      if (data) allTestimonials.push(...data);
    } catch (_) {}
  }
  const localList = getLocalPendingTestimonials();
  allTestimonials.push(...localList);

  // 2. Identificar candidatos de prueba
  const testItems = allTestimonials.filter(isTestTestimonial);
  const idsToPurge = new Set(testItems.map(t => String(t.id)).filter(Boolean));
  idsToPurge.add(SEED_TEST_UUID);
  idsToPurge.add('test-pending-cloudinary-1');

  // 3. Ejecutar borrado con destrucción de comprobante en Cloudinary
  for (const id of idsToPurge) {
    try {
      const delRes = await adminDeleteTestimonial(id);
      if (delRes && delRes.success) {
        result.totalPurged++;
        result.purgedIds.push(id);
        if (delRes.cloudinaryDeleted) {
          result.cloudinaryDeleted++;
        }
      }
    } catch (err) {
      console.warn('Error purgando ID:', id, err);
    }
  }

  // 4. Limpieza masiva directa en Supabase si está disponible
  if (isSupabaseConfigured && supabase) {
    try {
      await supabase
        .from('testimonios')
        .delete()
        .or(`id.eq.${SEED_TEST_UUID},gamertag.eq.@gamer_test_cloud,gamertag.ilike.%test%,testimonio_texto.ilike.%test de auditoría%,comprobante_captura_url.ilike.%unsplash.com%`);
    } catch (_) {}
  }

  // 5. Limpieza garantizada de LocalStorage
  try {
    if (typeof localStorage !== 'undefined') {
      const raw = localStorage.getItem(LOCAL_PENDING_TESTIMONIALS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          const clean = parsed.filter(t => !isTestTestimonial(t) && !idsToPurge.has(String(t?.id)));
          localStorage.setItem(LOCAL_PENDING_TESTIMONIALS_KEY, JSON.stringify(clean));
        }
      }
    }
  } catch (_) {}

  return result;
}

// =========================================================================
// =========================================================================
// STRIKES & INFRACCIONES API HELPERS (CON TOLERANCIA A MIGRACIÓN PENDIENTE)
// =========================================================================

const LOCAL_STRIKES_KEY = 'instaplay_strikes_cache';

export let isStrikesSchemaPending = false;

export function getLocalStrikes(clienteId) {
  try {
    const raw = localStorage.getItem(LOCAL_STRIKES_KEY);
    const all = raw ? JSON.parse(raw) : [];
    if (!clienteId) return all;
    return all.filter(s => String(s.cliente_id) === String(clienteId));
  } catch {
    return [];
  }
}

export function saveLocalStrike(clienteId, strike) {
  try {
    const raw = localStorage.getItem(LOCAL_STRIKES_KEY);
    const all = raw ? JSON.parse(raw) : [];
    const newRecord = {
      id: `local-strike-${Date.now()}`,
      cliente_id: clienteId,
      motivo_infraccion: strike.motivo_infraccion || strike.motivo || 'Infracción de reglas',
      descripcion: strike.descripcion || '',
      evidencia_url: strike.evidencia_url || '',
      puntos_strike: parseInt(strike.puntos_strike, 10) || 1,
      registrado_por: strike.registrado_por || 'admin',
      created_at: new Date().toISOString()
    };
    all.unshift(newRecord);
    localStorage.setItem(LOCAL_STRIKES_KEY, JSON.stringify(all));
    return newRecord;
  } catch {
    return null;
  }
}

export function deleteLocalStrike(strikeId) {
  try {
    const raw = localStorage.getItem(LOCAL_STRIKES_KEY);
    const all = raw ? JSON.parse(raw) : [];
    const filtered = all.filter(s => String(s.id) !== String(strikeId));
    localStorage.setItem(LOCAL_STRIKES_KEY, JSON.stringify(filtered));
  } catch {
    // ignore
  }
}

export async function adminGetClientStrikes(clienteId) {
  if (!isSupabaseConfigured || !supabase) {
    return getLocalStrikes(clienteId);
  }

  try {
    const { data, error } = await supabase
      .from('cliente_strikes')
      .select('*')
      .eq('cliente_id', clienteId)
      .order('created_at', { ascending: false });

    if (error) {
      if (error.code === 'PGRST205' || error.message?.includes('schema cache')) {
        isStrikesSchemaPending = true;
        console.warn('[SUPABASE]: La tabla cliente_strikes aún no existe en Supabase (PGRST205). Utilizando caché local.');
        return getLocalStrikes(clienteId);
      }
      console.warn('Error al obtener strikes del cliente:', error);
      return getLocalStrikes(clienteId);
    }

    isStrikesSchemaPending = false;
    // Si Supabase devuelve datos, mezclamos con cualquier strike local no sincronizado
    const local = getLocalStrikes(clienteId);
    const remoteIds = new Set((data || []).map(d => d.id));
    const unsynced = local.filter(l => !remoteIds.has(l.id));
    return [...unsynced, ...(data || [])];
  } catch (err) {
    console.warn('Excepción al consultar strikes:', err);
    return getLocalStrikes(clienteId);
  }
}

export async function adminAddClientStrike(clienteId, strikePayload) {
  const motivo = strikePayload.motivo_infraccion || strikePayload.motivo || 'Infracción de reglas';
  const puntos = parseInt(strikePayload.puntos_strike, 10) || 1;

  if (!isSupabaseConfigured || !supabase) {
    const newStrike = saveLocalStrike(clienteId, { ...strikePayload, motivo_infraccion: motivo });
    const allStrikes = getLocalStrikes(clienteId);
    const totalScore = allStrikes.reduce((acc, s) => acc + (parseInt(s.puntos_strike, 10) || 1), 0);
    return { newStrike, totalScore, isBanned: totalScore >= 3, isLocalFallback: true };
  }

  try {
    // 1. Intentar insertar el strike en Supabase
    const { data: newStrike, error: strikeErr } = await supabase
      .from('cliente_strikes')
      .insert([{
        cliente_id: clienteId,
        motivo_infraccion: motivo,
        descripcion: strikePayload.descripcion || '',
        evidencia_url: strikePayload.evidencia_url || '',
        puntos_strike: puntos,
        registrado_por: strikePayload.registrado_por || 'admin'
      }])
      .select()
      .single();

    if (strikeErr) {
      if (strikeErr.code === 'PGRST205' || strikeErr.message?.includes('schema cache')) {
        isStrikesSchemaPending = true;
        console.warn('[SUPABASE]: Tabla cliente_strikes no encontrada. Guardando strike en local storage.');
        const localStrike = saveLocalStrike(clienteId, { ...strikePayload, motivo_infraccion: motivo });
        const allStrikes = getLocalStrikes(clienteId);
        const totalScore = allStrikes.reduce((acc, s) => acc + (parseInt(s.puntos_strike, 10) || 1), 0);
        const isBanned = totalScore >= 3;

        // Intentar actualizar estado en clientes si aplica baneo
        if (isBanned) {
          try {
            await supabase.from('clientes').update({ estado: 'BANEADO' }).eq('id', clienteId);
          } catch {}
        }
        return { newStrike: localStrike, totalScore, isBanned, isLocalFallback: true };
      }
      throw strikeErr;
    }

    // 2. Calcular total de strikes del cliente
    const allStrikes = await adminGetClientStrikes(clienteId);
    const totalScore = allStrikes.reduce((acc, s) => acc + (parseInt(s.puntos_strike, 10) || 1), 0);
    const isBanned = totalScore >= 3;

    // 3. Actualizar el registro del cliente
    const updatePayload = {
      strikes_count: totalScore,
      is_banned: isBanned,
      ...(isBanned ? { estado: 'BANEADO' } : {})
    };

    try {
      await supabase
        .from('clientes')
        .update(updatePayload)
        .eq('id', clienteId);
    } catch (updateErr) {
      console.warn('Advertencia al actualizar columnas en clientes:', updateErr);
      if (isBanned) {
        await supabase.from('clientes').update({ estado: 'BANEADO' }).eq('id', clienteId);
      }
    }

    return { newStrike, totalScore, isBanned };
  } catch (err) {
    if (err.code === 'PGRST205' || err.message?.includes('schema cache')) {
      isStrikesSchemaPending = true;
      const localStrike = saveLocalStrike(clienteId, { ...strikePayload, motivo_infraccion: motivo });
      const allStrikes = getLocalStrikes(clienteId);
      const totalScore = allStrikes.reduce((acc, s) => acc + (parseInt(s.puntos_strike, 10) || 1), 0);
      return { newStrike: localStrike, totalScore, isBanned: totalScore >= 3, isLocalFallback: true };
    }
    throw err;
  }
}

export async function adminDeleteClientStrike(strikeId, clienteId, puntos) {
  deleteLocalStrike(strikeId);

  if (!isSupabaseConfigured || !supabase) {
    const allStrikes = getLocalStrikes(clienteId);
    const totalScore = allStrikes.reduce((acc, s) => acc + (parseInt(s.puntos_strike, 10) || 1), 0);
    const isBanned = totalScore >= 3;
    return { strikes_count: totalScore, is_banned: isBanned, estado: isBanned ? 'BANEADO' : 'ACTIVO' };
  }

  try {
    await supabase
      .from('cliente_strikes')
      .delete()
      .eq('id', strikeId);
  } catch (delErr) {
    console.warn('Aviso: no se pudo eliminar strike de Supabase (posiblemente era local):', delErr);
  }

  // Recalcular
  const allStrikes = await adminGetClientStrikes(clienteId);
  const totalScore = allStrikes.reduce((acc, s) => acc + (parseInt(s.puntos_strike, 10) || 1), 0);
  const isBanned = totalScore >= 3;

  try {
    await supabase
      .from('clientes')
      .update({
        strikes_count: totalScore,
        is_banned: isBanned,
        ...(isBanned ? { estado: 'BANEADO' } : { estado: 'ACTIVO' })
      })
      .eq('id', clienteId);
  } catch {}

  return { strikes_count: totalScore, is_banned: isBanned, estado: isBanned ? 'BANEADO' : 'ACTIVO' };
}

// =========================================================================
// PROMOCIONES API HELPERS (index.html & instaplay-os-sysadmin.html)
// =========================================================================

export const DEFAULT_ADMIN_PROMOTION = {
  id: 'promo-renovacion-admin-25',
  titulo: 'PROMO RENOVACIÓN RELÁMPAGO',
  badge: '⚡ EXCLUSIVO RENOVACIÓN',
  badge_texto: '⚡ EXCLUSIVO RENOVACIÓN',
  codigo_cupon: 'FLASH-8USD',
  descripcion: 'Tarifa preferencial de renovación a $8 USD para clientes recurrentes gestionada desde el panel administrativo.',
  precio_promo: 8.00,
  precio_regular: 10.00,
  divisa: 'USD',
  cupos_totales: 25,
  cupos_disponibles: 25,
  is_activa: true,
  is_active: true,
  destino: 'admin',
  fecha_fin: null,
  link_whatsapp: 'https://wa.me/51920271596?text=¡Hola!%20Deseo%20aprovechar%20la%20Promo%20de%20Renovación%20a%20$8%20USD%20con%20cupón%20[FLASH-8USD]'
};

export const DEFAULT_WEB_PROMOTION = {
  id: 'promo-bienvenida-web-2026',
  titulo: 'PROMO BIENVENIDA GAMER',
  badge: '🎮 PRIMER MES DE JUEGO',
  badge_texto: '🎮 PRIMER MES DE JUEGO',
  codigo_cupon: 'GAMER-8USD',
  descripcion: '¡Comienza a jugar hoy mismo! Acceso ilimitado a GeForce NOW y soporte prioritario a tarifa especial para nuevos usuarios.',
  precio_promo: 8.00,
  precio_regular: 10.00,
  divisa: 'USD',
  cupos_totales: 25,
  cupos_disponibles: 25,
  is_activa: true,
  is_active: true,
  destino: 'web',
  fecha_fin: null,
  link_whatsapp: 'https://wa.me/51920271596?text=¡Hola!%20Deseo%20aprovechar%20la%20Promo%20Bienvenida%20Gamer%20a%20$8%20USD%20con%20cupón%20[GAMER-8USD]'
};

// Mantenemos DEFAULT_PROMOTION por compatibilidad histórica
export const DEFAULT_PROMOTION = DEFAULT_WEB_PROMOTION;

/**
 * Detecta si una promoción corresponde a promociones de prueba históricas o temporales
 */
export function isTestPromotion(p) {
  if (!p) return false;
  const idStr = String(p.id || '');
  const title = String(p.titulo || '').toUpperCase().trim();
  const coupon = String(p.codigo_cupon || '').toUpperCase().trim();

  if (coupon === 'FLASH-8USD' || coupon === 'GAMER-8USD') return true;
  if (title === 'PROMO RENOVACIÓN RELÁMPAGO' || title === 'PROMO BIENVENIDA GAMER') return true;
  if (idStr === 'promo-renovacion-admin-25' || idStr === 'promo-bienvenida-web-2026' || idStr === 'promo-renovacion-25') return true;
  if (title.startsWith('[TEST]') || coupon.startsWith('TEST-')) return true;

  return false;
}

// Clave para almacenamiento persistente local de promociones (fallback resiliente)
const LOCAL_PROMOS_KEY = 'instaplay_custom_promotions_v2';
const LOCAL_DELETED_PROMOS_KEY = 'instaplay_deleted_promotions_v2';
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function getDeletedPromotionIds() {
  try {
    if (typeof localStorage === 'undefined') return [];
    const raw = localStorage.getItem(LOCAL_DELETED_PROMOS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (_) {
    return [];
  }
}

function markPromotionDeleted(id) {
  try {
    if (typeof localStorage === 'undefined' || !id) return;
    const list = getDeletedPromotionIds();
    const strId = String(id);
    if (!list.includes(strId)) {
      list.push(strId);
      localStorage.setItem(LOCAL_DELETED_PROMOS_KEY, JSON.stringify(list));
    }
  } catch (_) {}
}

function unmarkPromotionDeleted(id) {
  try {
    if (typeof localStorage === 'undefined' || !id) return;
    const list = getDeletedPromotionIds().filter(item => String(item) !== String(id));
    localStorage.setItem(LOCAL_DELETED_PROMOS_KEY, JSON.stringify(list));
  } catch (_) {}
}

function getLocalCustomPromotions() {
  try {
    if (typeof localStorage === 'undefined') return [];
    const raw = localStorage.getItem(LOCAL_PROMOS_KEY);
    const list = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(list)) return [];
    // Limpieza automática en caliente de promociones de prueba
    const clean = list.filter(p => !isTestPromotion(p));
    if (clean.length !== list.length) {
      localStorage.setItem(LOCAL_PROMOS_KEY, JSON.stringify(clean));
    }
    return clean;
  } catch (_) {
    return [];
  }
}

function saveLocalCustomPromotion(promo) {
  try {
    if (typeof localStorage === 'undefined' || !promo || isTestPromotion(promo)) return;
    const list = getLocalCustomPromotions().filter(p => String(p.id) !== String(promo.id));
    list.unshift(promo);
    localStorage.setItem(LOCAL_PROMOS_KEY, JSON.stringify(list));
  } catch (_) {}
}

function removeLocalCustomPromotion(id) {
  try {
    if (typeof localStorage === 'undefined' || !id) return;
    const list = getLocalCustomPromotions().filter(p => String(p.id) !== String(id));
    localStorage.setItem(LOCAL_PROMOS_KEY, JSON.stringify(list));
  } catch (_) {}
}

/**
 * Obtiene exclusivamente las promociones dirigidas a los usuarios y visitantes
 * de la página principal (index.html). NUNCA retorna promociones exclusivas del
 * panel administrativo ni de renovación interna ni promociones de prueba.
 */
export async function fetchPublicPromotions() {
  const allPromos = await adminGetPromotions();

  // Filtrar estrictamente: solo activas y con destino 'web' o 'ambos'
  const publicPromos = (allPromos || []).filter(p => {
    if (isTestPromotion(p)) return false;
    const isActive = !!(p.is_activa || p.is_active);
    if (!isActive) return false;

    const destino = (p.destino || '').toLowerCase().trim();

    // Promociones explícitamente asignadas al panel admin
    if (destino === 'admin' || destino === 'admin_panel' || destino === 'crm') {
      return false;
    }

    // Salvaguarda: si el título indica renovación y no tiene destino web explícito
    const titulo = (p.titulo || '').toUpperCase();
    if (titulo.includes('RENOVACIÓN') && destino !== 'web' && destino !== 'ambos') {
      return false;
    }

    return true;
  });

  return publicPromos;
}

/**
 * Obtiene todas las promociones (tanto las de la web pública como las exclusivas
 * del panel admin para renovación/CRM), para control total del administrador.
 */
export async function adminGetPromotions() {
  const localPromos = getLocalCustomPromotions();
  const deletedIds = getDeletedPromotionIds();
  let dbPromos = [];

  if (isSupabaseConfigured && supabase) {
    try {
      const { data, error } = await supabase
        .from('promociones')
        .select('*')
        .order('created_at', { ascending: false });

      if (!error && data) {
        dbPromos = data;
      } else if (error) {
        console.warn('Aviso: Consulta a promociones en Supabase:', error.message);
      }
    } catch (err) {
      console.warn('Excepción al obtener promociones admin:', err);
    }
  }

  // Filtrar promociones eliminadas por el administrador o de prueba
  const filteredDb = dbPromos.filter(p => !deletedIds.includes(String(p.id)) && !isTestPromotion(p));
  const filteredLocal = localPromos.filter(p => !deletedIds.includes(String(p.id)) && !isTestPromotion(p));

  // Fusionar: los creados localmente que no existan en BD se añaden al inicio
  const dbIds = new Set(filteredDb.map(p => String(p.id)));
  const merged = [
    ...filteredLocal.filter(lp => !dbIds.has(String(lp.id))),
    ...filteredDb
  ];

  return merged.map(p => {
    const badgeVal = p.badge || p.badge_texto || 'PROMO EXCLUSIVA';
    const isActive = p.is_activa ?? p.is_active ?? true;

    // Normalizar destino
    let normDestino = p.destino;
    if (!normDestino) {
      const isRenovacion = (p.titulo || '').toUpperCase().includes('RENOVACIÓN');
      normDestino = isRenovacion ? 'admin' : 'web';
    }
    if (!['web', 'admin', 'ambos'].includes(normDestino)) normDestino = 'web';

    return {
      ...p,
      badge: badgeVal,
      badge_texto: badgeVal,
      codigo_cupon: p.codigo_cupon || `PROMO-${(p.titulo || 'VIP').replace(/[^a-zA-Z0-9]/g, '').slice(0, 8).toUpperCase()}`,
      fecha_fin: p.fecha_fin || null,
      destino: normDestino,
      is_active: isActive,
      is_activa: isActive
    };
  });
}

export async function adminCreatePromotion(promoPayload) {
  const badgeVal = promoPayload.badge || promoPayload.badge_texto || 'PROMO EXCLUSIVA';
  const destinoVal = promoPayload.destino && ['web', 'admin', 'ambos'].includes(promoPayload.destino)
    ? promoPayload.destino
    : 'web';

  const payloadToSave = {
    ...promoPayload,
    badge: badgeVal,
    destino: destinoVal,
    fecha_fin: promoPayload.fecha_fin || null,
    is_activa: promoPayload.is_activa ?? promoPayload.is_active ?? true
  };
  delete payloadToSave.badge_texto;
  delete payloadToSave.is_active;

  let createdItem = {
    id: `promo-custom-${Date.now()}`,
    ...payloadToSave,
    badge: badgeVal,
    badge_texto: badgeVal,
    destino: destinoVal,
    fecha_fin: payloadToSave.fecha_fin,
    is_active: payloadToSave.is_activa,
    is_activa: payloadToSave.is_activa,
    created_at: new Date().toISOString()
  };

  if (isSupabaseConfigured && supabase) {
    try {
      let { data, error } = await supabase
        .from('promociones')
        .insert([payloadToSave])
        .select()
        .single();

      if (error && (error.message?.includes('destino') || error.message?.includes('fecha_fin') || error.message?.includes('codigo_cupon') || error.code === 'PGRST204')) {
        console.warn('Aviso: Columna(s) no existen aún en tabla promociones. Reintentando con payload compatible...', error.message);
        const fallbackPayload = { ...payloadToSave };
        if (error.message?.includes('destino')) delete fallbackPayload.destino;
        if (error.message?.includes('fecha_fin') || error.code === 'PGRST204') delete fallbackPayload.fecha_fin;
        if (error.message?.includes('codigo_cupon')) delete fallbackPayload.codigo_cupon;

        const retry = await supabase
          .from('promociones')
          .insert([fallbackPayload])
          .select()
          .single();
        data = retry.data;
        error = retry.error;
      }

      if (!error && data) {
        createdItem = {
          ...data,
          badge: data.badge || badgeVal,
          badge_texto: data.badge || badgeVal,
          destino: data.destino || destinoVal,
          fecha_fin: data.fecha_fin !== undefined ? data.fecha_fin : payloadToSave.fecha_fin,
          is_active: data.is_activa ?? true,
          is_activa: data.is_activa ?? true
        };
      } else if (error) {
        console.warn('Supabase no pudo guardar promo (usando persistencia local resiliente):', error.message);
      }
    } catch (err) {
      console.warn('Excepción al insertar promo en Supabase (usando persistencia local resiliente):', err);
    }
  }

  unmarkPromotionDeleted(createdItem.id);
  saveLocalCustomPromotion(createdItem);
  notifyPromotionsUpdated({ action: 'create', promo: createdItem });
  return createdItem;
}

export async function adminUpdatePromotion(id, updates) {
  const badgeVal = updates.badge || updates.badge_texto;
  const payloadToSave = { ...updates };
  if (badgeVal) {
    payloadToSave.badge = badgeVal;
  }
  delete payloadToSave.badge_texto;
  if (updates.is_active !== undefined && updates.is_activa === undefined) {
    payloadToSave.is_activa = updates.is_active;
  }
  delete payloadToSave.is_active;

  if (isSupabaseConfigured && supabase) {
    try {
      if (UUID_REGEX.test(id)) {
        let { data, error } = await supabase
          .from('promociones')
          .update(payloadToSave)
          .eq('id', id)
          .select()
          .single();

        if (error && (error.message?.includes('destino') || error.message?.includes('fecha_fin') || error.message?.includes('codigo_cupon') || error.code === 'PGRST204')) {
          console.warn('Aviso update promo: reintentando sin columnas nuevas...', error.message);
          const fallbackUpdates = { ...payloadToSave };
          if (error.message?.includes('destino')) delete fallbackUpdates.destino;
          if (error.message?.includes('fecha_fin') || error.code === 'PGRST204') delete fallbackUpdates.fecha_fin;
          if (error.message?.includes('codigo_cupon')) delete fallbackUpdates.codigo_cupon;

          await supabase
            .from('promociones')
            .update(fallbackUpdates)
            .eq('id', id)
            .select()
            .single();
        }
      } else {
        await supabase
          .from('promociones')
          .update(payloadToSave)
          .eq('titulo', 'PROMO RENOVACIÓN RELÁMPAGO');
      }
    } catch (err) {
      console.warn('Error actualizando promo en Supabase (usando persistencia local):', err);
    }
  }

  // Actualizar también en caché local si existe
  const localList = getLocalCustomPromotions();
  const existing = localList.find(p => String(p.id) === String(id));
  if (existing) {
    Object.assign(existing, payloadToSave, {
      is_active: payloadToSave.is_activa ?? existing.is_activa,
      is_activa: payloadToSave.is_activa ?? existing.is_activa,
      badge: payloadToSave.badge ?? existing.badge,
      badge_texto: payloadToSave.badge ?? existing.badge,
      destino: payloadToSave.destino ?? existing.destino ?? 'web',
      fecha_fin: payloadToSave.fecha_fin !== undefined ? payloadToSave.fecha_fin : existing.fecha_fin
    });
    saveLocalCustomPromotion(existing);
  }

  notifyPromotionsUpdated({ action: 'update', id, updates: payloadToSave });
  return { id, ...payloadToSave };
}

export async function adminTogglePromotion(id, isActiva) {
  return adminUpdatePromotion(id, { is_activa: isActiva });
}

export async function adminDeletePromotion(id) {
  markPromotionDeleted(id);
  removeLocalCustomPromotion(id);

  if (isSupabaseConfigured && supabase) {
    try {
      if (UUID_REGEX.test(id)) {
        const { error } = await supabase
          .from('promociones')
          .delete()
          .eq('id', id);
        if (error && error.code !== 'PGRST205') {
          console.warn('Aviso Supabase delete:', error.message);
        }
      } else {
        // Borrar por id, código de cupón o título
        await supabase
          .from('promociones')
          .delete()
          .or(`id.eq.${id},codigo_cupon.eq.${id},titulo.eq.${id}`);
      }
    } catch (err) {
      console.warn('Error al eliminar promo en Supabase:', err);
    }
  }

  notifyPromotionsUpdated({ action: 'delete', id });
  return true;
}

// =========================================================================
// SINCRONIZACIÓN EN TIEMPO REAL & CROSS-TAB BROADCAST
// =========================================================================
const PROMOS_BROADCAST_CHANNEL = 'instaplay_promos_sync_channel';
let localBroadcastChannel = null;
try {
  if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
    localBroadcastChannel = new BroadcastChannel(PROMOS_BROADCAST_CHANNEL);
  }
} catch (_) {}

export function notifyPromotionsUpdated(detail = {}) {
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('instaplay:promos-updated', { detail }));
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('instaplay_promos_last_sync', String(Date.now()));
      }
      if (localBroadcastChannel) {
        localBroadcastChannel.postMessage({ type: 'PROMOS_CHANGED', timestamp: Date.now(), detail });
      }
    }
  } catch (err) {
    console.warn('Aviso notifyPromotionsUpdated:', err);
  }
}

export function subscribePromotionsLocalSync(callback) {
  if (typeof window === 'undefined' || typeof callback !== 'function') return () => {};

  const onCustomEvent = (e) => callback(e.detail || {});
  const onStorageEvent = (e) => {
    if (e.key === 'instaplay_promos_last_sync' || e.key === LOCAL_PROMOS_KEY) {
      callback({ source: 'storage' });
    }
  };
  const onBroadcastMsg = (e) => {
    if (e.data && e.data.type === 'PROMOS_CHANGED') {
      callback(e.data.detail || {});
    }
  };

  window.addEventListener('instaplay:promos-updated', onCustomEvent);
  window.addEventListener('storage', onStorageEvent);
  if (localBroadcastChannel) {
    localBroadcastChannel.addEventListener('message', onBroadcastMsg);
  }

  return () => {
    window.removeEventListener('instaplay:promos-updated', onCustomEvent);
    window.removeEventListener('storage', onStorageEvent);
    if (localBroadcastChannel) {
      localBroadcastChannel.removeEventListener('message', onBroadcastMsg);
    }
  };
}

export function subscribeToPromotionsRealtime(callback) {
  if (!isSupabaseConfigured || !supabase) return null;
  try {
    const channel = supabase
      .channel('realtime:public:promociones')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'promociones' },
        (payload) => {
          notifyPromotionsUpdated({ source: 'supabase_realtime', payload });
          if (callback) callback(payload);
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          // Suscrito a eventos de postgres en tiempo real
        }
      });
    return channel;
  } catch (err) {
    console.warn('Aviso al suscribir realtime de promociones:', err);
    return null;
  }
}

/**
 * Genera un prompt estructurado y de alta conversión para que cualquier IA (ChatGPT, Gemini,
 * Claude, Midjourney, Canva AI, etc.) cree el banner publicitario gráfico y los copies
 * de la promoción, integrando de forma obligatoria el código de cupón y el link web oficial.
 */
export function buildPromoBannerAiPrompt(p, origin = '') {
  const currentOrigin = origin || (typeof window !== 'undefined' && window.location ? window.location.origin : 'https://instaplaylatam.com');
  const codigoCupon = p.codigo_cupon || `PROMO-${(p.titulo || 'VIP').replace(/[^a-zA-Z0-9]/g, '').slice(0, 8).toUpperCase()}`;
  const regular = parseFloat(p.precio_regular || 10).toFixed(2);
  const promoPrice = parseFloat(p.precio_promo || 8).toFixed(2);
  const ahorro = Math.max(0, (parseFloat(regular) - parseFloat(promoPrice))).toFixed(2);
  const pct = parseFloat(regular) > 0 ? Math.round(((parseFloat(regular) - parseFloat(promoPrice)) / parseFloat(regular)) * 100) : 0;
  const webPromoLink = `${currentOrigin}/?promo=${encodeURIComponent(codigoCupon)}#promociones`;
  const cuposDisponibles = p.cupos_disponibles ?? 'Cupos Limitados';
  const cuposTotales = p.cupos_totales ?? 25;
  const badgeText = p.badge || p.badge_texto || `🔥 -${pct}% OFF`;

  let vigenciaTexto = 'Vigencia limitada por orden de llegada hasta agotar cupos';
  if (p.fecha_fin) {
    const d = new Date(p.fecha_fin);
    vigenciaTexto = `Válido hasta el ${d.toLocaleDateString()} a las ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }

  return `🎨 PROMPT DE ALTA CONVERSIÓN // GENERACIÓN DE BANNER PUBLICITARIO (INSTAPLAY LATAM)

Actúa como Director de Arte Publicitario y Diseñador Gráfico Senior especializado en Videojuegos, Esports y Cloud Gaming. 
Diseña el concepto visual publicitario completo (listo para artes en formato Feed 1:1 [1080x1080] y Formato Vertical Stories/Reels 9:16 [1080x1920]) para la siguiente promoción oficial de InstaPlay Latam:

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🎮 DATOS OFICIALES DE LA OFERTA:
• Título de la Campaña: "${p.titulo}"
• Insignia / Badge: ${badgeText}
• Código de Cupón: [${codigoCupon}]
• Precio Promocional: $${promoPrice} USD (Precio Regular: $${regular} USD)
• Descuento / Ahorro: -${pct}% OFF (Ahorro directo de $${ahorro} USD)
• Enlace Web de Canje: ${webPromoLink}
• Disponibilidad en Vivo: ${cuposDisponibles} de ${cuposTotales} cupos
• Límite / Vigencia: ${vigenciaTexto}
• Descripción: ${p.descripcion || 'Acceso inmediato a Cloud Gaming de alto rendimiento sin descargas ni tarjetas gráficas caras.'}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🎯 ESTRUCTURA VISUAL OBLIGATORIA DEL BANNER:
1. TITULAR PRINCIPAL: Tipografía gamer bold con efecto neón: "${p.titulo}".
2. PRECIO DESTACADO: Enorme destaque tipográfico de "$${promoPrice} USD" tachando el precio regular "$${regular} USD".
3. CAJA DE CUPÓN LLAMATIVA: Destacar en recuadro flotante de neón: "CÓDIGO EXCLUSIVO: ${codigoCupon}" con la indicación "CANJEALO EN NUESTRA WEB".
4. ENLACE / CALL TO ACTION (CTA): "Haz clic en el enlace para canjear tu cupón: ${webPromoLink}" o "Link directo en el botón / bio".
5. CONTADOR DE URGENCIA (FOMO): "${cuposDisponibles} CUPOS RESTANTES · ${vigenciaTexto.toUpperCase()}".
6. PUNTOS FUERTES DEL SERVICIO:
   - ⚡ Cero Descargas: Juega al instante títulos AAA.
   - 💻 Hardware Modesto: Corre en cualquier laptop básica, PC antigua o smartphone.
   - 🎧 Soporte 1 a 1 en español en tiempo real.

🎨 IDENTIDAD GRÁFICA & PALETA DE COLORES:
- Estética Cyberpunk Retro Gaming de alta fidelidad.
- Fondo oscuro tecnológico (#020a04 o negro espacial profundo con texturas de microchips o líneas de datos).
- Acentos luminiscentes en verde neón (#00ff41), verde láser (#39ff14) y cian cibernético (#00f2fe).
- Controlador de videojuegos futurista o interfaz HUD holográfica flotando en el centro del diseño.

📸 PROMPT PARA GENERADOR DE IMÁGENES (Midjourney v6 / DALL-E 3 / Flux / Ideogram):
"High-end esports promotional banner background, floating luminous futuristic video game controller with neon green (#00ff41) and electric cyan neon accents, sleek dark sci-fi HUD holographic interfaces, clean negative space for typography and advertising copy, commercial game launch advertisement, cinematic lighting, photorealistic 8k octane render."
`;
}



